import { useRef, useState } from 'react';
import { RULES_VERSION, createMatch, type Replay, type Side } from '../../engine';
import { t } from '../../i18n';
import { ShareButton, type ShareResult } from '../components/ShareLink';
import { deleteReplay, loadReplays, parseReplayFile, type SavedReplay } from '../../app/saves';

export function ReplaysScreen({
  onBack,
  onOpen,
  onExport,
  onShare,
}: {
  onBack: () => void;
  onOpen: (replay: Replay, names: Readonly<Record<Side, string>>) => void;
  onExport: (replay: Replay) => void;
  /** Makes a link anyone can open to watch the replay (when the site hosts online play). */
  onShare?: (replay: Replay, names: Readonly<Record<Side, string>>) => Promise<ShareResult>;
}) {
  const [list, setList] = useState<SavedReplay[]>(loadReplays);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const importFile = async (file: File) => {
    const replay = parseReplayFile(await file.text());
    if (!replay) {
      setError(t('replays.importError', { version: RULES_VERSION }));
      return;
    }
    try {
      const seats = createMatch(replay.config, replay.seed).seats;
      onOpen(replay, { S: seats.S, N: seats.N });
    } catch {
      setError(t('replays.importError', { version: RULES_VERSION }));
    }
  };

  return (
    <main className="page">
      <header className="page-head">
        <button type="button" className="btn btn-small" onClick={onBack}>
          ← {t('menu.back')}
        </button>
        <h1>{t('replays.title')}</h1>
      </header>
      <div className="form-actions">
        <button type="button" className="btn" onClick={() => fileRef.current?.click()}>
          {t('replays.import')}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void importFile(f);
            e.target.value = '';
          }}
        />
      </div>
      {error && (
        <p className="notice" role="alert">
          {error}
        </p>
      )}
      {list.length === 0 ? (
        <p className="dim">{t('replays.empty')}</p>
      ) : (
        <ul className="replay-list">
          {list.map((r) => (
            <li key={r.id} className="replay-item">
              <div>
                <strong>{r.title}</strong>
                <span className="dim">
                  {new Date(r.savedAt).toLocaleString()} ·{' '}
                  {r.result
                    ? r.result.winner
                      ? t('dialog.matchEnd.winner', { name: r.names[r.result.winner] })
                      : t('dialog.matchEnd.draw')
                    : ''}
                </span>
              </div>
              <div className="replay-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-small"
                  onClick={() => onOpen(r.replay, r.names)}
                >
                  {t('replays.open')}
                </button>
                <button type="button" className="btn btn-small" onClick={() => onExport(r.replay)}>
                  {t('replays.export')}
                </button>
                {onShare && (
                  <ShareButton
                    create={() => onShare(r.replay, r.names)}
                    label={t('share.replay')}
                  />
                )}
                <button
                  type="button"
                  className="btn btn-small"
                  onClick={() => setList(deleteReplay(r.id))}
                >
                  {t('replays.delete')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
